/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/RFC3986.ts
 * @create:      2025-07-01 18:00:33.893
 * @modify:      2025-07-11 21:19:12.949
 * @version:     6.0.0
 * @times:       5
 * @lines:       87
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ReplaceAll } from "../string/ReplaceAll";

/**
 * Encodes a string according to RFC 3986 specifications.
 *
 * This function first applies `encodeURIComponent` to the input string,
 * then further encodes the characters `!`, `*`, `(`, `)`, and `'` to ensure
 * full compliance with RFC 3986.
 *
 * @param str - The input string to encode.
 * @returns The RFC 3986-compliant encoded string.
 * 
 * @see https://tools.ietf.org/html/rfc3986
 * 
 * @since 1.1.6
 * @version 2021-12-07
 */
export function EncodeByRFC3986(str: string) {
    let result = encodeURIComponent(str);

    result = ReplaceAll(result, "!", "%21");
    result = ReplaceAll(result, "*", "%2A");
    result = ReplaceAll(result, "(", "%28");
    result = ReplaceAll(result, ")", "%29");
    result = ReplaceAll(result, "'", "%27");

    return result;
}

/**
 * Decodes a URI component string according to RFC 3986 specifications.
 *
 * This function first replaces specific percent-encoded characters (`%21`, `%2A`, `%28`, `%29`, `%27`)
 * with their corresponding literal characters (`!`, `*`, `(`, `)`, `'`) before applying `decodeURIComponent`.
 * This ensures compatibility with RFC 3986, which allows these characters to remain unencoded in URI components.
 *
 * @param str - The percent-encoded URI component string to decode.
 * @returns The decoded string, with RFC 3986 reserved characters properly handled.
 * 
 * @since 1.1.11
 * @version 2021-12-07
 */
export function DecodeByRFC3986(str: string) {
    let result = ReplaceAll(str, "%21", "!");

    result = ReplaceAll(result, "%2A", "*");
    result = ReplaceAll(result, "%28", "(");
    result = ReplaceAll(result, "%29", ")");
    result = ReplaceAll(result, "%27", "'");

    result = decodeURIComponent(result);

    return result;
}
